import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Check, Lock, MessageSquare, Watch } from "lucide-react";
import { api, apiError, DEVICE_TYPE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { PairingCodeButton } from "@/components/PairingCode";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const STEPS = [
  { n: 1, label: "Account" },
  { n: 2, label: "ChatGPT" },
  { n: 3, label: "Device" },
  { n: 4, label: "Privacy" },
  { n: 5, label: "Ready" },
];

const PRIVACY_POINTS = [
  "Your original captures are always preserved, exactly as spoken.",
  "Your memories are private. No other user or admin can read them.",
  "Memory content is never included in analytics or logs.",
  "You can export or permanently delete your data at any time.",
  "MIA never asks for your ChatGPT password.",
];

export default function Setup() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [devices, setDevices] = useState([]);
  const [finishing, setFinishing] = useState(false);

  const loadDevices = async () => {
    try {
      const { data } = await api.get("/v1/devices");
      setDevices(data.devices);
    } catch { /* shown as not connected */ }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const resendVerification = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const finish = async () => {
    setFinishing(true);
    try {
      await api.post("/v1/setup/complete");
      await refreshUser();
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(apiError(e));
      setFinishing(false);
    }
  };

  const watch = devices.find((d) => d.device_type === "apple_watch");

  return (
    <Layout>
      <div className="mx-auto max-w-xl" data-testid="setup-wizard">
        <h1 className="font-display text-3xl font-medium tracking-tight">Let's set up your MIA.</h1>

        <ol className="mt-10 space-y-1" data-testid="setup-steps">
          {STEPS.map((s) => {
            const done = s.n < step || (s.n === 1);
            const active = s.n === step;
            return (
              <li key={s.n}>
                <button
                  type="button"
                  data-testid={`setup-step-${s.n}-tab`}
                  onClick={() => setStep(s.n)}
                  className={`flex w-full items-center gap-4 rounded-xl px-4 py-3 text-left transition-colors duration-200 ${
                    active ? "bg-secondary" : "hover:bg-secondary/50"
                  }`}
                >
                  <span className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs ${
                    done ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"
                  }`}>
                    {done ? <Check className="h-3.5 w-3.5" /> : s.n}
                  </span>
                  <span className={`text-sm ${active ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
                  {s.n === 1 && <span className="ml-auto text-xs text-muted-foreground">Complete</span>}
                </button>

                {active && (
                  <div className="mb-4 ml-4 mt-1 rounded-xl border border-border bg-card p-6" data-testid={`setup-step-${s.n}-panel`}>
                    {s.n === 1 && (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">Account created for</p>
                        <p className="text-sm font-medium" data-testid="setup-account-email">{user?.email}</p>
                        {user?.email_verified ? (
                          <Badge variant="secondary" className="gap-1" data-testid="setup-email-verified-badge">
                            <BadgeCheck className="h-3 w-3" /> Email verified
                          </Badge>
                        ) : (
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" data-testid="setup-email-pending-badge">Verification pending</Badge>
                            <Button variant="link" size="sm" className="h-auto p-0 text-xs" data-testid="resend-verification-button" onClick={resendVerification}>
                              Resend verification email
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {s.n === 2 && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <MessageSquare className="h-5 w-5 text-muted-foreground" />
                          <span className="text-sm font-medium">ChatGPT</span>
                          <Badge variant="outline" data-testid="setup-chatgpt-status">Coming Soon</Badge>
                        </div>
                        <p className="text-sm leading-relaxed text-muted-foreground">
                          Soon you'll ask ChatGPT things like "What did I tell MIA today?" — and it will answer from your MIA memory.
                          MIA will use official OpenAI integration mechanisms. We never ask for your ChatGPT password.
                        </p>
                        <Button disabled size="sm" data-testid="setup-connect-chatgpt-button">Connect ChatGPT</Button>
                      </div>
                    )}

                    {s.n === 3 && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <Watch className="h-5 w-5 text-muted-foreground" />
                          <span className="text-sm font-medium">Apple Watch</span>
                          {watch ? (
                            <Badge variant="secondary" data-testid="setup-watch-status-active">Active</Badge>
                          ) : (
                            <Badge variant="outline" data-testid="setup-watch-status">Not connected</Badge>
                          )}
                        </div>
                        {watch ? (
                          <p className="text-sm text-muted-foreground" data-testid="setup-watch-name">
                            {watch.device_name} · {DEVICE_TYPE_LABELS[watch.device_type]}
                          </p>
                        ) : (
                          <>
                            <p className="text-sm leading-relaxed text-muted-foreground">
                              The MIA watchOS app is in development. When it arrives, pairing takes seconds: generate a code here, enter it on your watch, done.
                            </p>
                            <PairingCodeButton onPaired={loadDevices} testId="setup-generate-pairing-code-button" />
                          </>
                        )}
                      </div>
                    )}

                    {s.n === 4 && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <Lock className="h-5 w-5 text-muted-foreground" />
                          <span className="text-sm font-medium">Review permissions</span>
                        </div>
                        <ul className="space-y-2" data-testid="setup-privacy-points">
                          {PRIVACY_POINTS.map((p) => (
                            <li key={p} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /> {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {s.n === 5 && (
                      <div className="space-y-3" data-testid="setup-ready-panel">
                        <p className="font-display text-lg font-medium">MIA is active.</p>
                        <p className="text-sm leading-relaxed text-muted-foreground">
                          Your private memory infrastructure is ready. Pair a device when the watchOS app launches,
                          and connect ChatGPT when the integration opens. You never need to visit this website again.
                        </p>
                      </div>
                    )}

                    <div className="mt-6 flex items-center justify-between">
                      <Button variant="ghost" size="sm" data-testid="setup-back-button"
                              onClick={() => setStep((v) => Math.max(1, v - 1))} disabled={step === 1}>
                        Back
                      </Button>
                      {step < 5 ? (
                        <Button size="sm" data-testid="setup-next-button" onClick={() => setStep((v) => Math.min(5, v + 1))}>
                          Continue
                        </Button>
                      ) : (
                        <Button size="sm" data-testid="setup-finish-button" onClick={finish} disabled={finishing}>
                          {finishing ? "Activating…" : "Open dashboard"}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </Layout>
  );
}
